import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toastUtils from '../utils/toastUtils';
import { i18n } from '../translate/i18n';

export const useKanbanAutomations = () => {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Carregar automações
  const fetchAutomations = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const { data } = await api.get('/kanban-automations', {
        params: {
          pageNumber: params.pageNumber || '1',
          active: params.active,
          triggerType: params.triggerType,
          actionType: params.actionType
        }
      });

      setAutomations(data.automations || []);
      return data;
    } catch (err) {
      console.error('Error fetching automations:', err);
      setError(err.message);
      toastUtils.error(i18n.t('kanban.messages.errorFetchingAutomations'), 'Erro no Kanban');
      return { automations: [], count: 0, hasMore: false };
    } finally {
      setLoading(false);
    }
  }, []);

  // Criar automação
  const createAutomation = useCallback(async (automationData) => {
    setLoading(true);

    try {
      const { data } = await api.post('/kanban-automations', automationData);
      
      setAutomations(prevAutomations => [data, ...prevAutomations]);
      toastUtils.success(i18n.t('kanban.messages.automationCreated'), 'Status Alterado');
      
      return data;
    } catch (err) {
      console.error('Error creating automation:', err);
      toastUtils.error(i18n.t('kanban.messages.errorCreatingAutomation'), 'Erro no Kanban');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Atualizar automação
  const updateAutomation = useCallback(async (automationId, updateData) => {
    setLoading(true);

    try {
      const { data } = await api.put(`/kanban-automations/${automationId}`, updateData);
      
      setAutomations(prevAutomations =>
        prevAutomations.map(automation =>
          automation.id === parseInt(automationId) ? { ...automation, ...data } : automation
        )
      );

      toastUtils.success(i18n.t('kanban.messages.automationUpdated'), 'Status Alterado');
      return data;
    } catch (err) {
      console.error('Error updating automation:', err);
      toastUtils.error(i18n.t('kanban.messages.errorUpdatingAutomation'), 'Erro no Kanban');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Deletar automação
  const deleteAutomation = useCallback(async (automationId) => {
    setLoading(true);

    try {
      await api.delete(`/kanban-automations/${automationId}`);
      
      setAutomations(prevAutomations =>
        prevAutomations.filter(automation => automation.id !== parseInt(automationId))
      );

      toastUtils.success(i18n.t('kanban.messages.automationDeleted'), 'Status Alterado');
    } catch (err) {
      console.error('Error deleting automation:', err);
      toastUtils.error(i18n.t('kanban.messages.errorDeletingAutomation'), 'Erro no Kanban');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Alternar status da automação (ativar/desativar)
  const toggleAutomation = useCallback(async (automationId, active) => {
    try {
      // Usar a rota específica de toggle
      const { data } = await api.put(`/kanban-automations/${automationId}/toggle`);

      setAutomations(prevAutomations =>
        prevAutomations.map(automation =>
          automation.id === parseInt(automationId)
            ? { ...automation, active: !automation.active }
            : automation
        )
      );

      toastUtils.success(data.active
        ? i18n.t('kanban.messages.automationEnabled')
        : i18n.t('kanban.messages.automationDisabled'), 'Status Alterado'
      );

      return data;
    } catch (err) {
      console.error('Error toggling automation:', err);
      toastUtils.error(i18n.t('kanban.messages.errorTogglingAutomation'), 'Erro no Kanban');
      throw err;
    }
  }, []);

  // Testar automação
  const testAutomation = useCallback(async (automationId, testContext = {}) => {
    setLoading(true);

    try {
      const { data } = await api.post(`/kanban-automations/${automationId}/test`, {
        testContext: testContext || {
          ticket: { id: 1, status: 'open', priority: 'medium' },
          task: { id: 1, status: 'todo', priority: 'medium' },
          user: { id: 1 }
        }
      });

      toastUtils.success(i18n.t('kanban.messages.automationTestSuccess'), 'Status Alterado');
      return data;
    } catch (err) {
      console.error('Error testing automation:', err);
      toastUtils.error(i18n.t('kanban.messages.errorTestingAutomation'), 'Erro no Kanban');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obter histórico de execuções (logs)
  const getAutomationHistory = useCallback(async (automationId, params = {}) => {
    try {
      const { data } = await api.get(`/kanban-automations/${automationId}/logs`, {
        params: {
          pageNumber: params.pageNumber || '1',
          limit: params.limit || '20'
        }
      });

      return data;
    } catch (err) {
      console.error('Error fetching automation history:', err);
      toastUtils.error(i18n.t('kanban.messages.errorFetchingHistory'), 'Erro no Kanban');
      return { logs: [], count: 0, hasMore: false };
    }
  }, []);

  // Obter estatísticas de automações
  const getAutomationStats = useCallback(async (automationId, days = 30) => {
    try {
      const { data } = await api.get(`/kanban-automations/${automationId}/stats`, {
        params: {
          days: days
        }
      });

      return data;
    } catch (err) {
      console.error('Error fetching automation stats:', err);
      return null;
    }
  }, []);

  // Validar regra de automação
  const validateRule = useCallback((ruleData) => {
    const errors = [];

    // Validações básicas
    if (!ruleData.name || ruleData.name.trim().length === 0) {
      errors.push(i18n.t('kanban.automation.validation.nameRequired'));
    }

    if (!ruleData.triggerType) {
      errors.push(i18n.t('kanban.automation.validation.triggerRequired'));
    }

    if (!ruleData.actions || !Array.isArray(ruleData.actions) || ruleData.actions.length === 0) {
      errors.push(i18n.t('kanban.automation.validation.actionRequired'));
    }

    // Validações específicas por tipo de ação
    if (ruleData.actions && ruleData.actions.length > 0) {
      const firstAction = ruleData.actions[0];
      const params = firstAction.parameters || {};

      switch (firstAction.type) {
        case 'move_ticket':
          if (!params.targetTagId) {
            errors.push('ID da tag de destino é obrigatório');
          }
          break;
        case 'assign_user':
          if (!params.userId) {
            errors.push('ID do usuário é obrigatório');
          }
          break;
        case 'send_notification':
          if (!params.message) {
            errors.push('Mensagem da notificação é obrigatória');
          }
          break;
        case 'update_field':
          if (!params.field || params.value === undefined) {
            errors.push('Campo e valor são obrigatórios');
          }
          break;
        case 'create_task':
          if (!params.title) {
            errors.push('Título da tarefa é obrigatório');
          }
          break;
        case 'change_priority':
          if (!params.priority) {
            errors.push('Prioridade é obrigatória');
          }
          break;
        case 'set_due_date':
          if (!params.dueDate) {
            errors.push('Data de vencimento é obrigatória');
          }
          break;
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }, []);

  // Buscar tipos de trigger e ação disponíveis
  const getTriggerTypes = useCallback(() => [
    { value: 'ticket_moved', label: i18n.t('kanban.automation.triggerTypes.ticket_moved') },
    { value: 'task_completed', label: i18n.t('kanban.automation.triggerTypes.task_completed') },
    { value: 'time_based', label: i18n.t('kanban.automation.triggerTypes.time_based') },
    { value: 'field_changed', label: i18n.t('kanban.automation.triggerTypes.field_changed') },
    { value: 'tag_assigned', label: i18n.t('kanban.automation.triggerTypes.tag_assigned') },
    { value: 'user_assigned', label: i18n.t('kanban.automation.triggerTypes.user_assigned') }
  ], []);

  const getActionTypes = useCallback(() => [
    { value: 'move_ticket', label: i18n.t('kanban.automation.actionTypes.move_ticket') },
    { value: 'assign_user', label: i18n.t('kanban.automation.actionTypes.assign_user') },
    { value: 'send_notification', label: i18n.t('kanban.automation.actionTypes.send_notification') },
    { value: 'update_field', label: i18n.t('kanban.automation.actionTypes.update_field') },
    { value: 'create_task', label: i18n.t('kanban.automation.actionTypes.create_task') },
    { value: 'change_priority', label: i18n.t('kanban.automation.actionTypes.change_priority') },
    { value: 'set_due_date', label: i18n.t('kanban.automation.actionTypes.set_due_date') }
  ], []);

  // Carregar automações na inicialização
  useEffect(() => {
    fetchAutomations();
  }, [fetchAutomations]);

  return {
    automations,
    loading,
    error,
    fetchAutomations,
    createAutomation,
    updateAutomation,
    deleteAutomation,
    toggleAutomation,
    testAutomation,
    getAutomationHistory,
    getAutomationStats,
    validateRule,
    getTriggerTypes,
    getActionTypes
  };
};

export default useKanbanAutomations;